import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  Plus, 
  FileCode,
  Clock,
  Upload,
  X,
  Lock
} from 'lucide-react';
import { supabase } from '../../../shared/lib/supabase';
import { toast } from 'sonner';
import { detectFileFormat, parsePDBFile as parsePDBContent, parseCIFFile as parseCIFContent } from '../../molecules/lib/molecularParser';

interface MoleculeFile {
  id: string;
  name?: string;
  file_name: string;
  file_size: number;
  file_url: string;
  file_type: string;
  atom_count?: number;
  residue_count?: number;
  chain_count?: number;
  created_at: string;
  updated_at?: string;
  expires_at: string;
  ran_odyssey?: boolean;
  odyssey_job_id?: string;
  isbasepdb?: boolean | null;
  odyssey_job?: {
    job_id: string;
    name: string;
  };
}

interface FilePreparerSidebarProps {
  projectId: string;
  onOpenMolecule: (moleculeId: string, name: string) => void;
  onClose: () => void;
  isVisible?: boolean;
}

export function FilePreparerSidebar({ projectId, onOpenMolecule, onClose, isVisible = true }: FilePreparerSidebarProps) {
  // ALL HOOKS MUST COME FIRST - no code between them
  const [molecules, setMolecules] = useState<MoleculeFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [preferredFileType, setPreferredFileType] = useState<'pdb' | 'cif'>('pdb');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subscriptionRef = useRef<any>(null);
  const mountedRef = useRef(true);
  
  // State for naming modal
  const [showNameModal, setShowNameModal] = useState(false);
  const [pendingMolecule, setPendingMolecule] = useState<any>(null);
  const [moleculeName, setMoleculeName] = useState('');
  const [hasBasePDB, setHasBasePDB] = useState(false);
  
  // Debug effect to verify useEffect is working
  // useEffect(() => {
  //   console.log('🔧 DEBUG: Basic useEffect running in FilePreparerSidebar');
  //   console.log('🔧 DEBUG: projectId:', projectId, 'isVisible:', isVisible);
  //   
  //   return () => {
  //     console.log('🔧 DEBUG: Basic useEffect cleanup');
  //   };
  // }, [projectId, isVisible]);
  
  // Load molecules effect - only run when we have a projectId
  useEffect(() => {
    if (!projectId || !isVisible) {
      return;
    }
    
    loadMolecules();
  }, [projectId, isVisible]);
  
  // Set up real-time subscription effect
  useEffect(() => {
    if (!projectId || !isVisible) {
      return;
    }
    
    // Clean up existing subscription first
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
    }
    
    const setupSubscription = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          return;
        }

        const channelName = `molecules-${projectId}-${user.id}`;
        
        subscriptionRef.current = supabase
          .channel(channelName)
          .on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'molecule_viewer',
            filter: `project_id=eq.${projectId}`
          }, () => {
            if (mountedRef.current) {
              loadMolecules();
            }
          })
          .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'molecule_viewer',
            filter: `project_id=eq.${projectId}`
          }, () => {
            if (mountedRef.current) {
              loadMolecules();
            }
          })
          .on('postgres_changes', {
            event: 'DELETE',
            schema: 'public',
            table: 'molecule_viewer',
            filter: `project_id=eq.${projectId}`
          }, () => {
            if (mountedRef.current) {
              loadMolecules();
            }
          })
          .subscribe((status) => {
            // Only log errors, not debug info
            if (status === 'CHANNEL_ERROR') {
              console.error('Error subscribing to molecule changes');
            } else if (status === 'TIMED_OUT') {
              console.error('Subscription timed out');
            }
          });
      } catch (error) {
        console.error('Error setting up subscription:', error);
      }
    };
    
    setupSubscription();
    
    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [projectId, isVisible]);
  
  // Component cleanup effect
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Load preferred file type from sessionStorage
  useEffect(() => {
    const savedFileType = sessionStorage.getItem(`project_${projectId}_fileType`) as 'pdb' | 'cif' | null;
    if (savedFileType && (savedFileType === 'pdb' || savedFileType === 'cif')) {
      setPreferredFileType(savedFileType);
    }
  }, [projectId]);
  
  // NOW we can have regular code after ALL hooks
  // console.log('🔧 DEBUG: FilePreparerSidebar render', { projectId, isVisible, moleculesCount: molecules.length });
  
  // Define loadMolecules function
  const loadMolecules = async () => {
    if (!mountedRef.current) {
      return;
    }
    
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Direct query to molecule_viewer table ordered by created_at descending
      // Remove user filter to allow team members to see each other's molecules
      const { data: projectMolecules, error } = await supabase
        .from('molecule_viewer')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }
      
      // Fetch odyssey_job data in batch to avoid N+1 queries
      const moleculesWithOdysseyData = [...(projectMolecules || [])];
      const odysseyJobIds = projectMolecules?.filter(m => m.odyssey_job_id).map(m => m.odyssey_job_id) || [];
      
      if (odysseyJobIds.length > 0) {
        try {
          const { data: odysseyJobs } = await supabase
            .from('odyssey_jobs')
            .select('job_id, name')
            .in('job_id', odysseyJobIds);
          
          // Create lookup map for fast access
          const odysseyJobMap = new Map();
          odysseyJobs?.forEach(job => odysseyJobMap.set(job.job_id, job));
          
          // Attach odyssey_job data to molecules
          moleculesWithOdysseyData.forEach(molecule => {
            if (molecule.odyssey_job_id && odysseyJobMap.has(molecule.odyssey_job_id)) {
              molecule.odyssey_job = odysseyJobMap.get(molecule.odyssey_job_id);
            }
          });
        } catch (error) {
          console.warn('Could not fetch odyssey job data:', error);
        }
      }
      
      if (mountedRef.current) {
        setMolecules(moleculesWithOdysseyData || []);
        // Check if there's already a base file uploaded (isbasepdb = true)
        const hasBase = moleculesWithOdysseyData?.some(molecule => molecule.isbasepdb === true) || false;
        setHasBasePDB(hasBase);
        
        // If there's a base file, detect its actual file type and update preferredFileType
        if (hasBase) {
          const baseFile = moleculesWithOdysseyData?.find(molecule => molecule.isbasepdb === true);
          if (baseFile && baseFile.file_type) {
            const actualFileType = baseFile.file_type.toLowerCase();
            if (actualFileType === 'pdb' || actualFileType === 'cif') {
              setPreferredFileType(actualFileType as 'pdb' | 'cif');
              // Update sessionStorage to match the actual file type
              sessionStorage.setItem(`project_${projectId}_fileType`, actualFileType);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading molecules:', error);
      if (mountedRef.current) {
        toast.error('Failed to load molecular files');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type based on project preference
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    
    const validExtensions = preferredFileType === 'pdb' 
      ? ['.pdb'] 
      : ['.cif', '.mmcif'];
    
    if (!validExtensions.includes(fileExtension)) {
      const expectedType = preferredFileType.toUpperCase();
      toast.error(`This project is configured for ${expectedType} files. Please select a ${expectedType} file.`);
      return;
    }

    // Check file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      return;
    }

    await uploadFile(file);
  };

  const parseStructureFile = async (file: File): Promise<{ atomCount: number; residueCount: number; chainCount: number }> => {
    try {
      const content = await file.text();
      const format = detectFileFormat(content);
      
      let parsedData;
      if (format === 'pdb') {
        parsedData = parsePDBContent(content, 'atomic');
      } else if (format === 'cif') {
        parsedData = parseCIFContent(content, 'atomic');
      } else {
        // Fallback to simple PDB parsing for unknown formats
        const lines = content.split('\n');
        const atoms = new Set<string>();
        const residues = new Set<string>();
        const chains = new Set<string>();
        
        lines.forEach(line => {
          if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
            const atomSerial = line.substring(6, 11).trim();
            atoms.add(atomSerial);
            
            const resSeq = line.substring(22, 26).trim();
            residues.add(resSeq);
            
            const chainId = line.substring(21, 22).trim();
            if (chainId) chains.add(chainId);
          }
        });
        
        return {
          atomCount: atoms.size,
          residueCount: residues.size,
          chainCount: chains.size || 1
        };
      }
      
      return {
        atomCount: parsedData.metadata.atomCount || 0,
        residueCount: parsedData.metadata.residueCount || 0,
        chainCount: parsedData.metadata.chainCount || 1
      };
    } catch (error) {
      console.error('Error parsing structure file:', error);
      return { atomCount: 0, residueCount: 0, chainCount: 0 };
    }
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    const toastId = toast.loading('Uploading molecular file...');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be signed in to upload files');
      }

      // Parse file for metadata (PDB or CIF)
      let metadata = { atomCount: 0, residueCount: 0, chainCount: 0 };
      const fileExt = file.name.toLowerCase().split('.').pop();
      if (fileExt === 'pdb' || fileExt === 'cif' || fileExt === 'mmcif') {
        metadata = await parseStructureFile(file);
        
        // Validate single chain requirement
        if (metadata.chainCount > 1) {
          const fileType = fileExt === 'pdb' ? 'PDB' : 'CIF';
          toast.error(`Multi-chain ${fileType} files are not supported. Found ${metadata.chainCount} chains, only 1 chain allowed. Please upload a single-chain ${fileType} file.`);
          setUploading(false);
          toast.dismiss(toastId);
          return;
        }
      }

      // Create unique storage path
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `${user.id}/${projectId}/${timestamp}_${sanitizedFileName}`;

      // Calculate expiry for unused molecules (7 days from now).
      // If Odyssey runs, records are preserved because they get an odyssey_job_id.
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);

      // FIRST: Create database record with temporary URL (required for storage RLS policy)
      const { data: moleculeData, error: dbError } = await supabase
        .from('molecule_viewer')
        .insert({
          user_id: user.id,
          project_id: projectId,
          file_name: file.name,
          file_size: file.size,
          file_url: '', // Temporary - will be updated after upload
          file_type: fileExt || 'pdb',
          atom_count: metadata.atomCount,
          residue_count: metadata.residueCount,
          chain_count: metadata.chainCount,
          storage_path: storagePath,
          expires_at: expiryDate.toISOString(),
          isbasepdb: true  // Mark this as a base file since it's uploaded from user's machine
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // SECOND: Now upload to Supabase Storage (RLS policy can now find the molecule_viewer record)
      const { error: uploadError } = await supabase.storage
        .from('molecule-files')
        .upload(storagePath, file, {
          contentType: 'text/plain',
          upsert: false
        });

      if (uploadError) {
        // If upload fails, clean up the database record
        await supabase
          .from('molecule_viewer')
          .delete()
          .eq('id', moleculeData.id);
        throw uploadError;
      }

      // THIRD: Get public URL and update the database record
      const { data: { publicUrl } } = supabase.storage
        .from('molecule-files')
        .getPublicUrl(storagePath);

      const { error: updateError } = await supabase
        .from('molecule_viewer')
        .update({ file_url: publicUrl })
        .eq('id', moleculeData.id);

      if (updateError) throw updateError;

      const fileType = (fileExt || 'pdb').toUpperCase();
      toast.success(`${fileType} file uploaded successfully`, { id: toastId });
      
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Set hasBasePDB to true to prevent further uploads
      setHasBasePDB(true);

      // Update preferred file type based on uploaded file
      const uploadedFileType = fileExt || 'pdb';
      setPreferredFileType(uploadedFileType as 'pdb' | 'cif');
      
      // Save file type to sessionStorage for persistence
      sessionStorage.setItem(`project_${projectId}_fileType`, uploadedFileType);
      
      // Refresh the list
      await loadMolecules();
      
      // Show naming modal for the newly uploaded molecule
      if (moleculeData) {
        setPendingMolecule(moleculeData);
        setMoleculeName('');
        setShowNameModal(true);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload file', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmName = async () => {
    if (!moleculeName.trim() || !pendingMolecule) return;
    
    try {
      // Update the molecule with the user-provided name
      const { error } = await supabase
        .from('molecule_viewer')
        .update({ name: moleculeName.trim() })
        .eq('id', pendingMolecule.id);
      
      if (error) throw error;
      
      // Open the molecule with the new name
      onOpenMolecule(pendingMolecule.id, moleculeName.trim());
      
      // Close modal and reset state
      setShowNameModal(false);
      setPendingMolecule(null);
      setMoleculeName('');
      
      // Refresh the list to show the updated name
      await loadMolecules();
    } catch (error: any) {
      console.error('Error updating molecule name:', error);
      toast.error('Failed to save molecule name');
    }
  };

  const filteredMolecules = molecules.filter(molecule =>
    molecule.file_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Helper function to get the actual base file type for display
  const getBaseFileTypeForDisplay = (): string => {
    const baseFile = molecules.find(molecule => molecule.isbasepdb === true);
    if (baseFile && baseFile.file_type) {
      return baseFile.file_type.toUpperCase();
    }
    // Fallback to preferredFileType if no base file found
    return preferredFileType.toUpperCase();
  };

  // Don't render anything if not visible or no projectId
  if (!isVisible || !projectId) {
    return null;
  }

  return (
    <>
      {/* Naming Modal */}
      {showNameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4">Name Your Molecule</h2>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a name for this molecular structure (max 30 characters)
            </p>
            <input
              type="text"
              value={moleculeName}
              onChange={(e) => setMoleculeName(e.target.value.slice(0, 30))}
              maxLength={30}
              placeholder="e.g., Enzyme Design v1"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
                moleculeName.length === 30
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-purple-500'
              }`}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && moleculeName.trim()) {
                  handleConfirmName();
                }
              }}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {moleculeName.length}/30 characters
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={handleConfirmName}
                disabled={!moleculeName.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-64 sidebar-width-secondary bg-white flex flex-col h-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={preferredFileType === 'pdb' ? '.pdb' : '.cif,.mmcif'}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Header */}
      <div className="px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileCode className="h-4 w-4 text-gray-600" />
            <h3 className="text-base font-normal text-gray-900">Protein Design</h3>
          </div>
          <button
            onClick={onClose}
            className="ml-1 p-0.5 rounded-sm hover:bg-gray-200 transition-all flex-shrink-0"
            title="Close Protein Design sidebar"
          >
            <X className="h-4 w-4 text-gray-500 hover:text-gray-700" />
          </button>
        </div>
        
        {/* Upload PDB Button or Disabled Message */}
        {hasBasePDB ? (
          <div className="w-full bg-gray-100 text-gray-600 px-3 py-1.5 rounded-sm flex items-center justify-center gap-2 text-sm mb-1 mt-4">
            <Lock className="h-3 w-3" />
            Base {getBaseFileTypeForDisplay()} uploaded
          </div>
        ) : (
          <div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full bg-gray-800 text-white px-3 py-1.5 rounded-sm hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 text-sm mb-1 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="h-3 w-3" />
              {uploading ? 'Uploading...' : `Upload ${preferredFileType.toUpperCase()}`}
            </button>
          </div>
        )}
        
        {/* Expiration Notice */}
        <p className="text-xs text-gray-500 mb-3">*Designs not run through Odyssey will automatically expire 7 days after they are created</p>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search designs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Separator */}
      <div className="border-b border-gray-200"></div>

      {/* Molecules List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          </div>
        ) : filteredMolecules.length === 0 ? (
          <div className="text-center py-8 px-4">
            <FileCode className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {searchQuery ? 'No molecules found' : 'No molecules uploaded yet'}
            </p>
          </div>
        ) : (
          <div className="py-1">
            {filteredMolecules.map((molecule) => (
              <button
                key={molecule.id}
                onClick={() => onOpenMolecule(molecule.id, molecule.name || molecule.odyssey_job?.name || molecule.file_name)}
                className={`w-full px-4 py-2.5 transition-colors text-left group border-b border-gray-100 ${
                  molecule.ran_odyssey ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileCode className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-normal text-gray-900 truncate">
                        {molecule.name || molecule.odyssey_job?.name || molecule.file_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      {molecule.ran_odyssey ? (
                        <>
                          <Lock className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-700">
                            Odyssey Ran {formatRelativeTime(molecule.updated_at || molecule.created_at)}
                          </span>
                        </>
                      ) : (
                        <>
                          <Clock className="h-3 w-3 text-blue-500" />
                          <span>created {formatRelativeTime(molecule.created_at)}</span>
                        </>
                      )}
                      <span className="mx-1">•</span>
                      <span>{formatFileSize(molecule.file_size)}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      </div>
    </>
  );
}
